import clsx from "clsx";
import { CSSProperties, FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";

import { useBookingsBulkActions } from "@ryde/redux";
import {
  bookingCalendarCellIdDatePassengerSF,
  capitalize,
  cellBookingsSF,
  currentDateAtMidnightISO,
  DateHelpers,
  DrawerType,
  parseBookingCellId,
  selectedCellsBookingCalendarAtom,
  SINGLE_KEY,
  useDrawers,
  useTimezone,
} from "@ryde/shared";

import { BookingCalendarTableBodyCellBooking } from "./booking-calendar.table-body.cell.booking";

interface IBookingCalendarTableBodyCell {
  columnIndex: number;
  rowIndex: number;
  style: CSSProperties;
}

export const BookingCalendarTableBodyCell: FC<
  IBookingCalendarTableBodyCell
> = ({ columnIndex, rowIndex, style }) => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const cellId = useRecoilValue(
    bookingCalendarCellIdDatePassengerSF(`${columnIndex},${rowIndex}`),
  );
  const { openDrawer } = useDrawers();
  const { cleanupPreviousProductionData } = useBookingsBulkActions();

  const bookingDtos = useRecoilValue(cellBookingsSF(cellId));
  const todayDate = useMemo(
    () => currentDateAtMidnightISO(timezone),
    [timezone],
  );
  const { date: dateString } = parseBookingCellId(cellId);
  const { size: selectedCellsCount } = useRecoilValue(
    selectedCellsBookingCalendarAtom,
  );

  const [selectedCells, setSelectedCells] = useRecoilState(
    selectedCellsBookingCalendarAtom,
  );
  const isCellSelected = selectedCells.has(cellId);
  const isCellInPast = DateHelpers.isBefore(
    dateString,
    todayDate,
    timezone,
    "day",
  );

  const setCellSelected = (isSelect: boolean): void => {
    if (isSelect) {
      setSelectedCells(prevValue => new Set(prevValue.add(cellId)));
    } else {
      setSelectedCells(prevValue => {
        prevValue.delete(cellId);
        return new Set(prevValue);
      });
    }
  };

  const onSelect = (): void => {
    setCellSelected(true);
  };

  const onDeselect = (): void => {
    setCellSelected(false);
  };

  const onBookingClick = (): void => {
    const { passengerId, date } = parseBookingCellId(cellId);
    cleanupPreviousProductionData();
    openDrawer({
      drawerProps: {
        type: DrawerType.AddNewBulkBookings,
        passengerId,
        date,
      },
    });
  };

  return (
    <div
      data-e2e="calendar_cell"
      data-e2e-id={cellId}
      className={clsx({
        booking_cell: true,
        cell_selected: isCellSelected,
        show_plus_button: !!bookingDtos?.length,
        "booking_cell--in_past": isCellInPast,
      })}
      style={style}
    >
      <div className="calendar_cell-dto_list">
        {Boolean(bookingDtos.length) &&
          bookingDtos.map(dto => (
            <BookingCalendarTableBodyCellBooking key={dto.id} dto={dto} />
          ))}
      </div>
      <div className="calendar_cell-footer">
        <div className="calendar_cell-cta_wrapper">
          {!isCellSelected && (
            <button className="select_button" onClick={onSelect}>
              {t("booking.select")}
            </button>
          )}
          {isCellSelected && (
            <button className="select_button" onClick={onDeselect}>
              {t("booking.deselect")}
            </button>
          )}
          {(!selectedCellsCount ||
            (selectedCellsCount === 1 && isCellSelected)) && (
            <button className="add_booking_button" onClick={onBookingClick}>
              {`${capitalize(t(`booking.${SINGLE_KEY}`))}+`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
